import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { MyDataScope, ProfileFieldScope } from 'mormaii-web-presentation/my-data'

import { Button, TextFilter } from '../../../components/filters'
import { StatusBands } from '../../../components/status-bands'
import { Tabs } from '../../../components/tabs'
import { ErrorNotice, Loading, Panel } from '../../../components/ui'
import { ThemeChoice } from '../../../components/theme-choice'

import Css from './my-data.module.scss'

/**
 * Configuracoes, organizada por ESCOPO.
 *
 * As abas respondem "isto vale para quem": voce, a empresa que voce administra, ou a
 * plataforma inteira. Quais existem e decisao do scope, porque e regra de papel.
 *
 * Dentro de cada aba, cartoes independentes - e nao um formulario so: cada um salva
 * sozinho, e misturar trocar a senha com corrigir o telefone num unico "Salvar"
 * obrigaria a digitar a senha atual para mexer no telefone.
 *
 * O editor de faixas e o mesmo componente do detalhe do colaborador; o que muda e o
 * degrau da cadeia que ele edita, e a aba em que aparece diz qual e.
 */
export function MyDataView(props: ViewProps<MyDataScope>): JSX.Element {
    return (
        <Show when={!props.scope.loading || props.scope.tabs.length > 0} fallback={<Loading />}>
            <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
                <div class={Css.screen}>
                    {/* Uma aba so nao e escolha - a barra some em vez de mostrar um botao inerte. */}
                    <Show when={props.scope.showTabs}>
                        <Tabs
                            items={props.scope.tabs.map((tab) => ({
                                key: tab.key,
                                label: tab.label,
                                active: tab.active,
                                onSelect: () => void tab.onSelect()
                            }))}
                        />
                    </Show>

                    <Show when={props.scope.profileFields.length > 0}>
                        <Profile scope={props.scope} />
                    </Show>

                    {/* A mesma escolha do menu do nome, para quem a procurar aqui. */}
                    <Show when={props.scope.themes.length > 0}>
                        <Panel title="Aparência">
                            <div class={Css.panel}>
                                <p class={Css.appearanceHint}>
                                    Vale para este painel, em qualquer computador em que você entrar.
                                </p>
                                <div class={Css.appearance}>
                                    <ThemeChoice options={props.scope.themes} />
                                </div>
                            </div>
                        </Panel>
                    </Show>

                    <Show when={props.scope.showPassword}>
                        <Password scope={props.scope} />
                    </Show>

                    <Show when={props.scope.showBands && props.scope.bands}>
                        <StatusBands scope={props.scope.bands!} />
                    </Show>
                </div>
            </Show>
        </Show>
    )
}

function Profile(props: { scope: MyDataScope }): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSaveProfile()
    }

    return (
        <form onSubmit={submit}>
            <Panel title={props.scope.profileTitle}>
                <div class={Css.panel}>
                    <Show when={props.scope.profileError}>
                        {(message) => (
                            <p role="alert" class={Css.alert}>
                                {message()}
                            </p>
                        )}
                    </Show>

                    <div class={Css.grid}>
                        <For each={props.scope.profileFields}>
                            {(field) => <ProfileField scope={field} ufOptions={props.scope.ufOptions} />}
                        </For>
                    </div>

                    {/*
                        O radio de profissional de saude nao aparece para admin nem
                        para admin de empresa - quem decide e o scope, porque e regra
                        de papel e nao de desenho.
                    */}
                    <Show when={props.scope.showHealthProfessional}>
                        <fieldset class={Css.question}>
                            <legend class={Css.questionTitle}>É profissional da saúde?</legend>

                            <div class={Css.options}>
                                <Radio
                                    label="Sim"
                                    checked={props.scope.healthProfessional}
                                    onSelect={() => props.scope.onHealthProfessionalChanged(true)}
                                />
                                <Radio
                                    label="Não"
                                    checked={!props.scope.healthProfessional}
                                    onSelect={() => props.scope.onHealthProfessionalChanged(false)}
                                />
                            </div>
                        </fieldset>
                    </Show>

                    <div class={Css.footer}>
                        <Button type="submit" disabled={!props.scope.profileDirty || props.scope.profileBusy}>
                            {props.scope.profileBusy ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </div>
                </div>
            </Panel>
        </form>
    )
}

/**
 * Um campo do perfil.
 *
 * O `span` que o scope traz sao as classes de coluna do painel antigo, de uma grade
 * de dez colunas. Aqui a grade e de tres e os campos tem a mesma largura - com dois
 * campos por linha num laptop, larguras diferentes deixam o formulario irregular sem
 * ganhar nada. O `span` fica sem uso, e o scope nao muda por causa disso: ele
 * descreve o dado, e quantas colunas o campo ocupa e decisao de quem desenha.
 */
function ProfileField(props: { scope: ProfileFieldScope; ufOptions: string[] }): JSX.Element {
    return (
        <Show
            when={props.scope.kind === 'uf'}
            fallback={
                <div classList={{ [Css.dimmed]: props.scope.dimmed }}>
                    <TextFilter
                        label={props.scope.label}
                        placeholder={props.scope.placeholder}
                        value={props.scope.value}
                        readOnly={props.scope.readOnly}
                        disabled={props.scope.disabled}
                        onChange={(value) => props.scope.onChanged(value)}
                    />
                    <Show when={props.scope.error}>{(message) => <p class={Css.errorBelow}>{message()}</p>}</Show>
                </div>
            }
        >
            <label class={Css.field}>
                <span class={Css.label}>{props.scope.label}</span>
                <select
                    value={props.scope.value}
                    disabled={props.scope.disabled}
                    onChange={(event) => props.scope.onChanged(event.currentTarget.value)}
                    class={Css.input}
                >
                    <option value="">Selecione</option>
                    <For each={props.ufOptions}>{(uf) => <option value={uf}>{uf}</option>}</For>
                </select>
                <Show when={props.scope.error}>{(message) => <span class={Css.error}>{message()}</span>}</Show>
            </label>
        </Show>
    )
}

function Radio(props: { label: string; checked: boolean; onSelect: () => void }): JSX.Element {
    return (
        <label class={Css.option}>
            <input
                type="radio"
                name="health-professional"
                checked={props.checked}
                onChange={props.onSelect}
                class={Css.radio}
            />
            {props.label}
        </label>
    )
}

function Password(props: { scope: MyDataScope }): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSavePassword()
    }

    return (
        <form onSubmit={submit}>
            <Panel title="Trocar a senha">
                <div class={Css.panel}>
                    <Show when={props.scope.passwordError}>
                        {(message) => (
                            <p role="alert" class={Css.alert}>
                                {message()}
                            </p>
                        )}
                    </Show>

                    <div class={Css.passwordGrid}>
                        <Secret
                            label="Senha atual"
                            value={props.scope.currentPassword}
                            onChange={(value) => props.scope.onCurrentPasswordChanged(value)}
                        />
                        <Secret
                            label="Nova senha"
                            value={props.scope.newPassword}
                            onChange={(value) => props.scope.onNewPasswordChanged(value)}
                        />
                        <Secret
                            label="Confirme a nova senha"
                            value={props.scope.confirmPassword}
                            onChange={(value) => props.scope.onConfirmPasswordChanged(value)}
                        />
                    </div>

                    <div class={Css.footerPair}>
                        <Button
                            variant="ghost"
                            disabled={!props.scope.passwordDirty || props.scope.passwordBusy}
                            onClick={() => void props.scope.onCancelPassword()}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={!props.scope.passwordDirty || props.scope.passwordBusy}>
                            {props.scope.passwordBusy ? 'Salvando…' : 'Trocar a senha'}
                        </Button>
                    </div>
                </div>
            </Panel>
        </form>
    )
}

function Secret(props: { label: string; value: string; onChange: (value: string) => void }): JSX.Element {
    return (
        <label class={Css.field}>
            <span class={Css.label}>{props.label}</span>
            <input
                type="password"
                autocomplete={props.label === 'Senha atual' ? 'current-password' : 'new-password'}
                value={props.value}
                onInput={(event) => props.onChange(event.currentTarget.value)}
                class={Css.input}
            />
        </label>
    )
}
